import { ACTION, NUMBER, OBJECT } from '../../../utils/component-validations';
import {
  ACTION_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
  sortMetadata,
} from '../../../utils/spds/metadata';
import { and } from 'ember-truth-helpers';
import AlertBannerContainer from 'ember-simplepractice/components/spds/feedback/alert-banner/container';
import ImageCrop from 'ember-simplepractice/components/shared/image-crop';
import SpdsModal from 'ember-simplepractice/components/spds/modal/standard';
import Super from '../core/base.gjs';
import ToastContainer from 'ember-simplepractice/components/spds/feedback/toast/container';
import styles from './image-crop.module.scss';

const METADATA = sortMetadata({
  component: 'Spds::Modal::ImageCrop',
  name: 'Image Crop',
  description: 'A modal that allows cropping of profile images',
  meta: {
    ...pickArgs(ACTION_ARGS, ['onClose', 'onBeforeClose']).meta,
    onConfirm: {
      type: ACTION,
      description: 'Action to be called when the image is confirmed',
    },
    onClose: {
      type: ACTION,
      description: 'Action to be called when the modal is closed',
    },
    file: {
      type: OBJECT,
      description: 'The image file to be cropped',
    },
    minImgHeight: {
      type: NUMBER,
      description: 'Required minimum height for the image',
    },
    minImgWidth: {
      type: NUMBER,
      description: 'Required minimum width for the image',
    },
    recommendedMinImgHeight: {
      type: NUMBER,
      description: 'Recommended minimum height for the image',
    },
    recommendedMinImgWidth: {
      type: NUMBER,
      description: 'Recommended minimum width for the image',
    },
    viewportBaseSize: {
      type: NUMBER,
      description: 'The base size for the viewport',
      defaultValue: 200,
    },
    viewportWidthRatio: {
      type: NUMBER,
      description: 'The ratio of the viewport width',
      defaultValue: 1,
    },
    viewportHeightRatio: {
      type: NUMBER,
      description: 'The ratio of the viewport height',
      defaultValue: 1,
    },
  },
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/modal/image-crop.gjs',
  },
});

export default class SpdsModalImageCrop extends Super {
  static metadata = METADATA;
  baseClass = 'spds-modal-image-crop';
  styles = styles;

  get bannerContainerId() {
    return this.composeSubcomponentId('banner-container');
  }

  get viewportBaseSize() {
    return this.args.viewportBaseSize ?? getDefaultValueFromMetadata(METADATA, 'viewportBaseSize');
  }

  get viewportHeightRatio() {
    return (
      this.args.viewportHeightRatio ?? getDefaultValueFromMetadata(METADATA, 'viewportHeightRatio')
    );
  }

  get viewportWidthRatio() {
    return (
      this.args.viewportWidthRatio ?? getDefaultValueFromMetadata(METADATA, 'viewportWidthRatio')
    );
  }

  <template>
    {{#if @file}}
      <SpdsModal
        @id={{this.id}}
        @onClose={{@onClose}}
        @onBeforeClose={{@onBeforeClose}}
        @closeOnClickOutside={{@closeOnClickOutside}}
        @alignItems="center"
        class={{this.classes}}
        data-test-spds-modal-image-crop
        as |M|
      >
        <M.Header @title="Edit image" />
        <M.Body>
          <AlertBannerContainer @id={{this.bannerContainerId}} />
          <ImageCrop
            @file={{@file}}
            @isStrictRecommendation={{@isStrictRecommendation}}
            @minImgHeight={{@minImgHeight}}
            @minImgWidth={{@minImgWidth}}
            @onClose={{@onClose}}
            @onConfirm={{@onConfirm}}
            @recommendedMinImgHeight={{@recommendedMinImgHeight}}
            @recommendedMinImgWidth={{@recommendedMinImgWidth}}
            @viewportBaseSize={{this.viewportBaseSize}}
            @viewportHeightRatio={{this.viewportHeightRatio}}
            @viewportWidthRatio={{this.viewportWidthRatio}}
            as |ic|
          >
            <ic.banner />
            {{#if (and ic.imgWidth ic.imgHeight)}}
              <ic.preview />
              {{#if ic.isAllowedToZoom}}
                <ic.range-input />
              {{/if}}
            {{/if}}
            <ic.actions @excludePwStyle={{@excludePwStyle}} />
          </ImageCrop>
          <ToastContainer @local={{true}} />
        </M.Body>
      </SpdsModal>
    {{/if}}
  </template>
}

export const IMAGE_CROP_METADATA = SpdsModalImageCrop.metadata;
